import { create } from 'zustand';
import { Message, Conversation } from '../types';
import { users } from '../data/sampleData';

interface MessageStore {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  activeConversation: string | null;
  sendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (conversationId: string) => void;
  setActiveConversation: (conversationId: string | null) => void;
  startConversation: (participantIds: string[], caseId?: string) => string;
  deleteConversation: (conversationId: string) => void;
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  conversations: [
    {
      id: 'conv1',
      participants: ['1', '4'], // Between lawyer and client
      unreadCount: 2,
      caseId: '1',
    },
    {
      id: 'conv2',
      participants: ['1', '2'], // Between lawyer and paralegal
      unreadCount: 0,
    },
  ],
  messages: {
    conv1: [
      {
        id: 'msg1',
        senderId: '4',
        recipientId: '1',
        content: 'Do you have any updates on my case?',
        timestamp: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
        read: false,
        caseId: '1',
      },
      {
        id: 'msg2',
        senderId: '4',
        recipientId: '1',
        content: 'I received some new medical records. I\'ve attached them here for your review.',
        timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
        read: false,
        caseId: '1',
        attachments: [
          {
            id: 'att1',
            name: 'Medical_Records_2024.pdf',
            type: 'application/pdf',
            url: '#',
            uploadDate: new Date(Date.now() - 3600000).toISOString(),
            size: 2500000, // 2.5MB
          },
          {
            id: 'att2',
            name: 'X-Ray_Report.pdf',
            type: 'application/pdf',
            url: '#',
            uploadDate: new Date(Date.now() - 3600000).toISOString(),
            size: 1800000, // 1.8MB
          }
        ]
      },
      {
        id: 'msg3',
        senderId: '1',
        recipientId: '4',
        content: 'Thank you for sending these documents. I\'ll review them right away and get back to you with my analysis.',
        timestamp: new Date(Date.now() - 1800000).toISOString(), // 30 minutes ago
        read: true,
        caseId: '1',
      }
    ],
    conv2: [
      {
        id: 'msg4',
        senderId: '1',
        recipientId: '2',
        content: 'Can you help me prepare the discovery documents for the Johnson case?',
        timestamp: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
        read: true,
      },
      {
        id: 'msg5',
        senderId: '2',
        recipientId: '1',
        content: "Of course, I will start working on them right away.",
        timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
        read: true,
      },
    ],
  },
  activeConversation: null,

  sendMessage: (messageData) => {
    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      timestamp: new Date().toISOString(),
      read: false,
      ...messageData,
    };

    set(state => {
      // Update messages
      const conversationMessages = state.messages[messageData.conversationId] || [];
      const updatedMessages = {
        ...state.messages,
        [messageData.conversationId]: [...conversationMessages, newMessage],
      };

      // Update conversation
      const updatedConversations = state.conversations.map(conv => {
        if (conv.id === messageData.conversationId) {
          return {
            ...conv,
            lastMessage: newMessage,
            unreadCount: conv.unreadCount + (
              messageData.recipientId === users[0].id ? 1 : 0
            ),
          };
        }
        return conv;
      });

      return {
        messages: updatedMessages,
        conversations: updatedConversations,
      };
    });
  },

  markAsRead: (conversationId) => {
    set(state => {
      // Mark all messages as read
      const updatedMessages = {
        ...state.messages,
        [conversationId]: (state.messages[conversationId] || []).map(msg => ({
          ...msg,
          read: true,
        })),
      };

      // Update conversation unread count
      const updatedConversations = state.conversations.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            unreadCount: 0,
          };
        }
        return conv;
      });

      return {
        messages: updatedMessages,
        conversations: updatedConversations,
      };
    });
  },

  setActiveConversation: (conversationId) => {
    set({ activeConversation: conversationId });
    if (conversationId) {
      get().markAsRead(conversationId);
    }
  },

  startConversation: (participantIds, caseId) => {
    const conversationId = `conv-${Date.now()}`;
    const newConversation: Conversation = {
      id: conversationId,
      participants: participantIds,
      unreadCount: 0,
      caseId,
    };

    set(state => ({
      conversations: [...state.conversations, newConversation],
      messages: {
        ...state.messages,
        [conversationId]: [],
      },
      activeConversation: conversationId,
    }));

    return conversationId;
  },

  deleteConversation: (conversationId) => {
    set(state => {
      const { [conversationId]: deletedMessages, ...remainingMessages } = state.messages;
      return {
        conversations: state.conversations.filter(conv => conv.id !== conversationId),
        messages: remainingMessages,
        activeConversation: state.activeConversation === conversationId ? null : state.activeConversation,
      };
    });
  },
}));